import React, { useEffect } from 'react';
import {
  ForgeFilePicker,
  ForgeButton,
  ForgeButtonToggleGroup,
  ForgeButtonToggle,
  ForgeIcon,
} from '@tylertech/forge-react';
import { orderBy as _orderBy } from 'lodash';
import { I18n } from 'common/i18n';
import { FeatureFlags } from 'common/feature_flags';
import DragDropContainer, {
  makeDefaultDragDropElementWrapper,
  DragDropContainerType,
} from 'common/components/DragDropContainer';
import Attachment from '../attachment/Attachment';
import { AttachmentsInputDirectProps } from '../../../../containers/AttachmentsInputContainer';
import { FileUploadStatus } from 'common/dsmapi/ManageMetadataComponent/types';

const t = (key: string, scope = 'shared.dataset_management_ui.metadata_manage.attachments_input') =>
  I18n.t(key, { scope });

type alphabetizeOrderings = 'asc' | 'desc';

const AttachmentsInput: React.FunctionComponent<AttachmentsInputDirectProps> = ({
  files,
  buttonText,
  uploadAttachment,
  removeAttachment,
  editAttachment,
  reorderAttachments,
  initializeAttachmentsList,
}) => {
  useEffect(() => {
    if (files.length === 0) {
      initializeAttachmentsList();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Handle reordering of attachments
  const handleReorder = (newOrder: FileUploadStatus[]) => {
    reorderAttachments(newOrder);
  };

  // Alphabetize helper function
  const alphabetize = (direction: alphabetizeOrderings) => () => {
    reorderAttachments(_orderBy(files, (file) => file.uploadedFile.name.toLowerCase(), direction));
  };

  // Render attachment items
  const renderAttachment = (file: FileUploadStatus, index: number) => (
    <Attachment
      key={file.uploadId}
      idx={index}
      file={file}
      onRemove={() => removeAttachment(file.uploadId)}
      onEdit={(newName) => editAttachment(file.uploadId, newName)}
    />
  );

  // Prepare drag-drop items
  const listItems = files.map((file, index) => renderAttachment(file, index));

  // Render file picker component
  const renderFilePicker = () => (
    <ForgeFilePicker
      compact={true}
      id="attachment-file-picker"
      on-forge-file-picker-change={(event: CustomEvent) => {
        if (event.detail.legalFiles.length) {
          uploadAttachment(event.detail.legalFiles[0]);
        }
      }}
    >
      <ForgeButton
        className="add-attachment-button"
        variant="outlined"
        id="add-attachment-button"
        data-testid="metadata-component-add-attachment-button"
      >
        <span>{buttonText}</span>
        <ForgeIcon name="attach_file" />
      </ForgeButton>
    </ForgeFilePicker>
  );

  // Render sorting buttons if feature is enabled
  const renderSortingButtons = () => {
    if (!FeatureFlags.valueOrDefault('enable_attachment_reorder_alphabet', false)) {
      return null;
    }

    return (
      <ForgeButtonToggleGroup
        dense={true}
        stretch={true}
        data-testid="metadata-component-attachments-sorting-button-group"
      >
        <ForgeButtonToggle
          title={t('sort_asc')}
          value={t('sort_asc')}
          onClick={alphabetize('asc')}
          data-testid="metadata-component-attachments-sorting-ascending"
        >
          <ForgeIcon name="sort_alphabetical_ascending" />
        </ForgeButtonToggle>
        <ForgeButtonToggle
          title={t('sort_desc')}
          value={t('sort_desc')}
          onClick={alphabetize('desc')}
          data-testid="metadata-component-attachments-sorting-descending"
        >
          <ForgeIcon name="sort_alphabetical_descending" />
        </ForgeButtonToggle>
      </ForgeButtonToggleGroup>
    );
  };

  return (
    <div className="attachments-input-container">
      <div className="add-attachment-section">
        <div className="add-attachment">
          {renderFilePicker()}
          {renderSortingButtons()}
        </div>
      </div>
      {files.length > 0 && (
        <DragDropContainer
          type={DragDropContainerType.LIST}
          items={files}
          className="metadata-component-attachment-list"
          iconName="drag_handle"
          onDrop={handleReorder}
          childElements={makeDefaultDragDropElementWrapper(listItems)}
        />
      )}
    </div>
  );
};

export default AttachmentsInput;
